import { useSyncExternalStore } from "react";
import { Trophy, Lock, Check } from "lucide-react";
import { MISSIONS, getMissionProgress, type MissionId } from "@/lib/ambassadors/ambassadorEngine";

/**
 * MISIONES MUNDIALISTAS™ — gamified mission ladder. Visual rewards, XP,
 * badges, animated progress. Replaces the simpler ViralChallengesPanel.
 * Persists via existing bumpMission engine (localStorage today, ready for
 * server sync later).
 */

// PR-40C-G — copy without XP. The real XP ledger lives in `xp_grant_log`
// and is owned by the XP engine. This panel no longer fabricates XP
// values outside that ledger ("phantom XP").
const COPY: Record<MissionId, { title: string; desc: string }> = {
  invite_5:         { title: "Connector", desc: "Invita a 5 fans a tu nación" },
  share_3_clips:    { title: "Viral Engine", desc: "Comparte 3 clips virales" },
  nation_hype_dom:  { title: "Voz Nacional", desc: "Genera 100 pulsos de hype" },
  win_nation_war:   { title: "Comandante", desc: "Gana 3 nation wars" },
  referrals_10:     { title: "Reclutador", desc: "10 referidos confirmados" },
  lead_predictions: { title: "Oráculo", desc: "25 predicciones acertadas" },
};

const ORDER: MissionId[] = ["invite_5", "share_3_clips", "nation_hype_dom", "win_nation_war", "referrals_10", "lead_predictions"];

function subscribe(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  const handler = (e: StorageEvent) => { if (e.key?.includes("ambassadors:missions")) cb(); };
  window.addEventListener("storage", handler);
  const id = window.setInterval(cb, 1500);
  return () => { window.removeEventListener("storage", handler); window.clearInterval(id); };
}
function getSnap() { return JSON.stringify(getMissionProgress()); }

export function WorldCupMissions() {
  const snap = useSyncExternalStore(subscribe, getSnap, () => "[]");
  const progress = JSON.parse(snap) as ReturnType<typeof getMissionProgress>;
  const byId = new Map(progress.map((p) => [p.id, p]));

  return (
    <section
      aria-label="Misiones Mundialistas"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[oklch(0.12_0.05_60)] via-[oklch(0.10_0.04_30)] to-[oklch(0.08_0.03_300)] p-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,.65)]"
    >
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-300" />
          <h2 className="text-xs font-black uppercase tracking-[0.18em] text-white/90">Misiones Mundialistas</h2>
        </div>
      </header>

      <ol className="grid gap-2 sm:grid-cols-2">
        {ORDER.map((id, i) => {
          const def = MISSIONS.find((m) => m.id === id)!;
          const p = byId.get(id) ?? { progress: 0, completed: false };
          const pct = Math.min(100, Math.round((p.progress / def.goal) * 100));
          const prevDone = i === 0 ? true : (byId.get(ORDER[i - 1])?.completed ?? false);
          const locked = !prevDone && !p.completed;
          const tone = p.completed
            ? "from-emerald-400/30 to-emerald-500/10 border-emerald-400/40"
            : locked
              ? "from-white/[0.03] to-white/[0.01] border-white/10 opacity-70"
              : "from-orange-500/15 to-fuchsia-500/10 border-white/15";
          return (
            <li
              key={id}
              className={`relative overflow-hidden rounded-xl border bg-gradient-to-br ${tone} p-3 transition-all duration-300 hover:-translate-y-0.5`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-xl"
                  style={{ background: def.reward.glow + "33", boxShadow: `inset 0 0 0 1px ${def.reward.glow}66` }}
                >
                  {locked ? <Lock className="h-4 w-4 text-white/40" /> : p.completed ? <Check className="h-4 w-4 text-emerald-300" /> : <span>{def.reward.badge}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="truncate text-sm font-black tracking-tight text-white">{COPY[id].title}</div>
                  </div>
                  <div className="mt-0.5 text-[11px] text-white/55">{COPY[id].desc}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/40">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-amber-300 via-orange-500 to-rose-500 transition-all duration-700"
                        style={{ width: `${pct}%` }}
                        role="progressbar"
                        aria-valuenow={pct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                    <span className="text-[10px] font-black tabular-nums text-white/70">{p.progress}/{def.goal}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">
                      {p.completed ? "Desbloqueado" : locked ? "Bloqueado" : "En curso"}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}